import clsx from "clsx";
import mixpanel from "mixpanel-browser";
import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { useToastsContext } from "../../contexts/toasts";
import {
  CreateRefreshToken,
  HandleApiSignOut,
} from "../../graphql/auth/auth.mutations";
import useIsMobile from "../../hooks/useIsMobile";
import useStores from "../../hooks/useStores";
import useUserAuditLogs from "../../hooks/useUserAuditLogs";
import Button from "../Button";
import "./integrations.css";

const GoogleDocsLogo = "/imgs/blocks/google-docs.png";
const GoogleFormsLogo = "/imgs/blocks/google-forms.png";

// Scopes for Google Docs and Forms
// openid and email are required to get id_token for user info
const GOOGLE_DOCS_SCOPE = "openid email https://www.googleapis.com/auth/documents";
const GOOGLE_FORMS_SCOPE = "openid email https://www.googleapis.com/auth/forms.body";

type IntegrationDetails = {
  name?: string;
  email?: string;
  isConnected: boolean;
};

type IntegrationType = "google-docs" | "google-forms";

interface GoogleServiceCardProps {
  type: IntegrationType;
  title: string;
  logo: string;
  scope: string;
  details: IntegrationDetails;
  onSignIn: () => void;
  onSignOut: () => void;
  isLoading: boolean;
}

const GoogleServiceCard: React.FC<GoogleServiceCardProps> = ({
  type,
  title,
  logo,
  details,
  onSignIn,
  onSignOut,
  isLoading,
}) => {
  const isMobile = useIsMobile();

  return (
    <div className="border-gray-200 w-full rounded-xl border p-5">
      <div
        className={clsx(
          isMobile ? "flex-col" : "flex",
          "w-full items-center justify-between"
        )}
      >
        {/* Left side: Logo, title, and connection info */}
        <div className="flex items-center">
          <img loading="lazy" src={logo} className="h-6 w-6 object-contain" />
          <div className="pl-3">
            <div className="text-gray-700 text-[15px] font-medium">{title}</div>
            {details.isConnected && (
              <div className="mt-1">
                {details.name && (
                  <div className="text-gray-700 text-[14px] font-semibold">
                    {details.name}
                  </div>
                )}
                {details.email && (
                  <div className="text-gray-500 text-[13px]">
                    {details.email}
                  </div>
                )}
                {!details.name && !details.email && (
                  <div className="text-gray-500 text-[13px] italic">
                    Connected
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right side: Action button */}
        <div className={clsx(isMobile && "mt-4 w-full")}>
          {details.isConnected ? (
            <Button
              theme="secondary"
              type="button"
              buttonText={isLoading ? "Signing out..." : "Sign out"}
              padding="large"
              width="225"
              onClick={onSignOut}
              disabled={isLoading}
              trackingName={`button-clicked-${type}-integration-sign-out`}
            />
          ) : (
            <button
              onClick={onSignIn}
              type="button"
              disabled={isLoading}
              className={clsx(
                "login-with-google-btn w-[225px]",
                isLoading && "opacity-50 cursor-not-allowed"
              )}
            >
              {isLoading ? "Connecting..." : "Sign in with Google"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

function GoogleIntegration() {
  const { auth } = useStores();
  const { addToast } = useToastsContext();
  const { createUserAuditLog } = useUserAuditLogs();

  const [docsDetails, setDocsDetails] = useState<IntegrationDetails>({
    isConnected: false,
  });
  const [formsDetails, setFormsDetails] = useState<IntegrationDetails>({
    isConnected: false,
  });
  const [isDocsLoading, setIsDocsLoading] = useState(false);
  const [isFormsLoading, setIsFormsLoading] = useState(false);

  const loadIntegrationDetails = useCallback(() => {
    const integrations = auth.user?.integrations;

    // Google Docs
    if (integrations && integrations["google-docs"]) {
      setDocsDetails({
        name: integrations["google-docs"].name,
        email: integrations["google-docs"].email,
        isConnected: true,
      });
    } else {
      setDocsDetails({ isConnected: false });
    }

    // Google Forms
    if (integrations && integrations["google-forms"]) {
      setFormsDetails({
        name: integrations["google-forms"].name,
        email: integrations["google-forms"].email,
        isConnected: true,
      });
    } else {
      setFormsDetails({ isConnected: false });
    }
  }, [auth.user?.integrations]);

  useEffect(() => {
    loadIntegrationDetails();
  }, [loadIntegrationDetails]);

  const handleSignIn = async (type: IntegrationType, scope: string) => {
    if (!auth?.user?.id) return;

    const userId = auth.user.id;
    const setLoading =
      type === "google-docs" ? setIsDocsLoading : setIsFormsLoading;

    setLoading(true);

    // Track the click
    mixpanel.track(`${type}-integration-clicked`);
    createUserAuditLog({
      actionId: `${type}-integration-clicked`,
      actionType: "MISC",
      details: {},
    });

    try {
      // @ts-ignore - Google Identity Services
      const client = window.google.accounts.oauth2.initCodeClient({
        client_id:
          import.meta.env.MODE === "development"
            ? import.meta.env.VITE_APP_GOOGLE_CLIENT_ID_DEV
            : import.meta.env.VITE_APP_GOOGLE_CLIENT_ID_PROD,
        scope,
        ux_mode: "popup",
        callback: async (response: any) => {
          try {
            if (!response.code) {
              setLoading(false);
              return;
            }

            const res = await auth.apolloClient.mutate({
              mutation: CreateRefreshToken,
              variables: {
                tokenInput: {
                  code: response.code,
                  userId,
                  type,
                },
              },
            });

            if (res?.data?.createRefreshToken) {
              addToast(
                `${
                  type === "google-docs" ? "Google Docs" : "Google Forms"
                } connected successfully.`,
                { type: "success" }
              );

              mixpanel.track(`${type}-integration-enabled`, {
                source: "settings",
              });

              createUserAuditLog({
                actionId: `${type}-integration-enabled`,
                actionType: "MISC",
                details: { source: "settings" },
              });

              // Refresh user data and update local state
              await auth.fetch();
              loadIntegrationDetails();
            } else {
              addToast("Something went wrong. Please try again later.", {
                type: "error",
              });
            }
          } catch (error) {
            console.error(error);
            addToast("Something went wrong. Please try again later.", {
              type: "error",
            });
          } finally {
            setLoading(false);
          }
        },
      });

      client.requestCode();
    } catch (error) {
      console.error(error);
      addToast("Failed to initialize Google sign-in. Please try again.", {
        type: "error",
      });
      setLoading(false);
    }
  };

  const handleSignOut = async (type: IntegrationType) => {
    if (!auth?.user?.id) return;

    const userId = auth.user.id;
    const setLoading =
      type === "google-docs" ? setIsDocsLoading : setIsFormsLoading;
    const serviceName = type === "google-docs" ? "Google Docs" : "Google Forms";

    setLoading(true);

    try {
      const res = await auth.apolloClient.mutate({
        mutation: HandleApiSignOut,
        variables: {
          tokenInput: {
            userId,
            type,
          },
        },
      });

      if (res?.data?.handleApiSignOut) {
        addToast(`${serviceName} disconnected.`, { type: "success" });

        mixpanel.track(`${type}-integration-removed`);

        createUserAuditLog({
          actionId: `${type}-integration-removed`,
          actionType: "MISC",
          details: {},
        });

        // Refresh user data and update local state
        await auth.fetch();
        loadIntegrationDetails();
      } else {
        addToast("Something went wrong. Please try again later.", {
          type: "error",
        });
      }
    } catch (error) {
      console.error(error);
      addToast("Something went wrong. Please try again later.", {
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          Google Integrations
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Connect your Google account to export content to Google Docs and
          Google Forms.
        </p>
      </div>

      <GoogleServiceCard
        type="google-docs"
        title="Google Docs"
        logo={GoogleDocsLogo}
        scope={GOOGLE_DOCS_SCOPE}
        details={docsDetails}
        onSignIn={() => handleSignIn("google-docs", GOOGLE_DOCS_SCOPE)}
        onSignOut={() => handleSignOut("google-docs")}
        isLoading={isDocsLoading}
      />

      <GoogleServiceCard
        type="google-forms"
        title="Google Forms"
        logo={GoogleFormsLogo}
        scope={GOOGLE_FORMS_SCOPE}
        details={formsDetails}
        onSignIn={() => handleSignIn("google-forms", GOOGLE_FORMS_SCOPE)}
        onSignOut={() => handleSignOut("google-forms")}
        isLoading={isFormsLoading}
      />
    </div>
  );
}

export default observer(GoogleIntegration);
